<template>
  <OnexAlert :type="Notifications.error" :icon="mdiAlertCircle" v-if="responseError">
    {{ $t('L_error_occured_while_saving') }}
  </OnexAlert>
  <v-main class="mt-4 pt-0 ml-5 mr-2">
    <v-row class="mb-2">
      <v-col cols="12" font-weight-bold>
        <h4 class="firstLabel font-weight-bold">
          {{ $t('L_confirm_your_new_payment_withdrawal_day') }}
        </h4>
      </v-col>
    </v-row>
    <v-row class="blue-background pa-2 ma-0">
      <v-col cols="12" class="d-flex align-center">
        <h4 class="font-weight-bold">{{ $t('L_of_each_month', { date: getSelectedDate }) }}</h4>
        <v-spacer></v-spacer>
        <span>
          <OnexButton @click="goToWDStepOne">
            {{ $t('L_change') }}
          </OnexButton>
        </span>
      </v-col>
      <v-col cols="12" font-size--14 mb-4 v-if="dateIssueNextPayDateEarly === 3">
        {{ $t('L_because_your_current_draft_day_is_within_the_next') }}
      </v-col>
      <v-col cols="12" font-size--14 mb-4 v-if="dateIssueNextPayDateEarly === 1">
        {{ $t('L_because_you_have_chosen_a_date_in_the_past') }}
      </v-col>
      <v-col
        cols="12"
        font-size--14
        mb-4
        v-if="
          dateIssueNextPayDateEarly === 1 ||
          dateIssueNextPayDateEarly === 2 ||
          dateIssueNextPayDateEarly === 3
        "
      >
        {{
          $t('L_going_forward_your_next_payment_will_be_withdrawn_on_the', {
            dayth: getSelectedDate,
          })
        }}
      </v-col>
    </v-row>
    <v-row
      v-if="dateIssueNextPayDateEarly === 1 || dateIssueNextPayDateEarly === 3"
      class="warning-box mt-4 mb-4 pt-4 pb-4"
    >
      <v-col cols="12">
        <span class="pr-2 mb-4">
          <v-icon :color="'#f39c12'" size="large" class="font-size--20" :icon="mdiAlert" />
        </span>
        <span class="font-size--14" v-html="getConfirmationData" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <div>
          <LoadingIndicator :loading="!processCompleted" />
        </div>
      </v-col>
    </v-row>
    <!-- Button -->
    <v-row class="d-flex align-center justify-end py-4">
      <!-- <v-col xs0 sm0 md="4"> &nbsp; </v-col> -->
      <v-col cols="12" sm="6" md="4" class="pr-4">
        <OnexButton block variant="outlined" color="primary" @click="cancel" class="maxwidthButton">
          {{ $t('L_cancel') }}
        </OnexButton>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="pr-4">
        <OnexButton
          block
          class="maxwidthButton"
          @click="goToWDStepThree"
          :disabled="confirmDisabled"
          color="primary"
        >
          {{ $t('L_confirm') }}
        </OnexButton>
      </v-col>
    </v-row>
  </v-main>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { currency, formatDate } from '@/shared/util/formatters';
import { mapActions, mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { mdiAlert, mdiAlertCircle } from '@mdi/js';
import { Notifications, OnexAlert, OnexButton } from '@primerica/onex-ui-lib';
import LoadingIndicator from '../../../../../components/LoadingIndicator.vue';
import type { IPolicyStorePolicy } from '@/modules/policy/store/policyStore.model';

export const notSameDigits = (value: string) => {
  const regexp = /(\d)\1{7}/;
  return !regexp.test(value);
};

export const letterSpaceLetter = (value: string) => {
  const regexp = /^[a-zA-Z]+[\sa-zA-Z-.]{0,}[a-zA-Z]+$/;
  return regexp.test(value);
};

const componentOptions: ComponentOptions = {
  name: 'WDStepTwo',
  components: {
    OnexButton,
    LoadingIndicator,
    OnexAlert,
  },
  props: {
    daySelected: {
      type: String,
      required: true,
      default: '1',
    },
    dateIssueNextPayDateEarly: {
      type: Number,
      required: true,
      default: -1,
    },
    dateForNextPayment: {
      type: String,
      required: true,
      default: '1',
    },
  },
  data: () => ({
    persistent: true,
    dialog: false,
    confirmDisabled: false,
    responseError: false,
    mdiAlert,
    processCompleted: true,
    Notifications,
    mdiAlertCircle,
  }),
  computed: {
    ...mapStores(usePolicyStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, [
      'policies',
      'currentPolicy',
      'routeNameToNavigateBack',
      'selectedPolicyIndex',
      'bankNameResponse',
      'paymentData',
    ]),
    ...mapState(useGlobalStore, { locale: 'getLocale', dateFormat: 'getDateFormat' }),
    getConfirmationData() {
      let x = this.$t('L_by_clicking_confirm_you_are_agreeing_to_have_the_current_amount_due_of', {
        amount: this.formattedTotalPremium,
        account_num: this.getMaskedAccountNum,
      });
      x += ' ';
      return x;
    },
    getSelectedDate() {
      let x;
      x = this.daySelected;
      if (this.daySelected === '1' || this.daySelected === '21') {
        x += 'st ';
      } else if (this.daySelected === '2' || this.daySelected === '22') {
        x += 'nd ';
      } else if (this.daySelected === '3' || this.daySelected === '23') {
        x += 'rd ';
      } else {
        x += 'th ';
      }
      return x;
    },
    formattedTotalPremium() {
      return currency(
        `${
          (+this.currentPolicy?.modePremium || 0) +
          (+this.currentPolicy?.data?.annuityModePrem || 0)
        }`,
        2
      );
    },
    getMaskedAccountNum() {
      let x = this.currentPolicy?.paymentInfo?.accountNumber || '';
      x = x.replaceAll('*', '');
      return x;
    },
    isDateWithinThreeDaysOfDraftDate() {
      const currentWDDate = formatDate(this.currentPolicy?.nextDraftDate, 'MM/dd/yyyy');
      let day = 0;
      if (currentWDDate.length > 6) {
        // For MM/DD/YYYY format
        day = parseInt(currentWDDate.substr(3, 2), 10);
        // If we have DD/MM/YYYY use substr(0,2)
      }

      let iDaySelected = 0;
      if (this.daySelected !== null && this.daySelected !== '') {
        iDaySelected = parseInt(this.daySelected, 10);
      }
      return iDaySelected - day <= 3;
    },
    getNextPaymentDate() {
      // Returns the month day not week day
      const y = new Date().getDate();
      return y;
    },
    isCanadaPolicy() {
      return this.currentPolicy.isCanadaPolicy;
    },
  },
  methods: {
    ...mapActions(usePolicyStore, ['setMonthlyWithDrawalDay', 'changePolicy']),
    goToWDStepOne() {
      this.$emit('goBackToWDStepOne');
      try {
        const i = this.$dynatrace.enterAction('ReviewWithdrawalDay_ChangeDay_Selected');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    },
    async goToWDStepThree() {
      if (this.processCompleted === false) {
        return;
      }
      this.processCompleted = false;
      if (!this.confirmDisabled) {
        try {
          const i = this.$dynatrace.enterAction('ReviewWithdrawalDay_Confirm_Selected');
          this.$dynatrace.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
        this.confirmDisabled = true;
        let tDaySelected = this.daySelected;
        if (tDaySelected.length === 1) {
          tDaySelected = `0${tDaySelected}`;
        }
        const currentContractId = this.policyData?.contractId;
        const response = await this.setMonthlyWithDrawalDay(tDaySelected);
        if (
          response &&
          (response?.status === 200 || response?.status === 201 || response?.status === 204)
        ) {
          this.responseError = false;
          await this.policyStore.getPolicies();
          await this.policyStore.getPolicyOverview();
          // Find the index that has the same contractId and use it to set the policyData
          const index = this.policies.findIndex(
            (policy: IPolicyStorePolicy) => policy.contractId === currentContractId
          );
          this.changePolicy({ selectedIndex: index });
          this.$emit('goToWDStepThree');
          this.confirmDisabled = false;
          // WD Confirmation Page Displayed
          try {
            const i = this.$dynatrace.enterAction('WithdrawalDayConfirmation_PageDisplayed');
            this.$dynatrace.leaveAction(i);
          } catch (_error) {
            /* empty */
          }
          // Successfully done
          try {
            const i = this.$dynatrace.enterAction('ChangeWithdrawalDay_stop');
            this.$dynatrace.leaveAction(i);
          } catch (_error) {
            /* empty */
          }
        } else {
          this.responseError = true;
          this.confirmDisabled = false;
          try {
            const errorCodeDescription = `${response?.status}_${response?.data.ReturnMessage}`;
            const i = this.$dynatrace.enterAction(
              `WithdrawalDayConfirmation_Failure_${errorCodeDescription}`
            );
            this.$dynatrace.leaveAction(i);
          } catch (_error) {
            /* empty */
          }
        }
      }
      this.processCompleted = true;
    },
    cancel() {
      this.$emit('cancelDialog');
      try {
        const i = this.$dynatrace.enterAction('ReviewWithdrawalDay_Cancel_Selected');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style>
.warning-box {
  background-color: #fdf3e2 !important;
  border-top: 4px solid #f39c12;
  margin-left: 0px !important;
  border-left: 0px;
  border-right: 0px;
  border-bottom: 0px;
  padding: 2px 10px 4px 10px;

  a {
    font-size: 16px;
  }
}

.bg-light-grey {
  background-color: #f5f5f5;
}

.blueBox {
  padding: 16px 24px 24px 24px;
  border-top: 8px solid #3399ff;
  border-left: 2px solid #3399ff;
  border-right: 2px solid #3399ff;
  border-bottom: 2px solid #3399ff;
  background-color: #e2f0fe;
}

.gray-border-box {
  border: 2px solid #e3e3e3;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: #ffffff;
}

.green-border-box {
  border: 2px solid #39ac4b;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: #d8f8dd;
}

.ml10 {
  margin-left: 10px;
}

.blueText {
  color: #0c6dca;
}

.LRBottom {
  border-left: 2px solid black;
  border-bottom: 2px solid black;
  border-right: 2px solid black;
}

.bottomBorder {
  border-bottom: 2px solid black;
}

.emptyLine {
  height: 50px;
}

.emptyLine2 {
  height: 30px;
}

.checkBlueText {
  color: #385d81;
}

.pLpR50 {
  padding-left: 50px;
  padding-right: 50px;
}

.spaceBelow10 {
  margin-bottom: 10px;
}

.spaceToRight {
  margin-right: 15px !important;
}

.maxwidthButton {
  max-width: 300px;
}

.maxwidthButton2 {
  max-width: 295px;
  width: 295px;
  font-size: 16px;
}

.centerText {
  margin: auto;
}

#bank_name {
  color: #000000 !important;
}

label.v-label.v-label--active.v-label--is-disabled.theme--light {
  color: #000000 !important;
}

.mouseCursor {
  cursor: pointer;
}

.blackText {
  color: #000000 !important;
}

.checkImg {
  width: 500px;
  margin: auto;
}

.mobileCheckImg {
  width: 224px;
  margin: auto;
}

.centerButton {
  margin: auto;
}

.labelBankName {
  color: rgba(0, 0, 0, 0.54);
  font-size: 12px;
}

.valueBankName {
  font-size: 16px;
  height: 24px;
  margin-bottom: 64px;
}

.clickable {
  cursor: pointer;
}

.blue_label {
  color: #02579e;
}

.gray-box {
  background-color: darkgray !important;
  cursor: default !important;
}
</style>
